import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Unsubscribe from "@/pages/Unsubscribe";
import ListingDetail from "@/pages/ListingDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Neighbourhood from "@/pages/community/Neighbourhood";
import LocalBusinesses from "@/pages/community/LocalBusinesses";
import CommunityEvents from "@/pages/community/CommunityEvents";
import BuyersGuide from "@/pages/BuyersGuide";
import Calculators from "@/pages/Calculators";
import NeighbourhoodBuzzer from "@/pages/NeighbourhoodBuzzer";
import HomeEvaluation from "@/pages/HomeEvaluation";
import BuyingTips from "@/pages/BuyingTips";
import BuyingTipDetail from "@/pages/BuyingTipDetail";
import SellingTips from "@/pages/SellingTips";
import SellingTipDetail from "@/pages/SellingTipDetail";
import AdminDashboard from "@/components/admin/AdminDashboard";
import AuthCallback from "@/components/AuthCallback";
import { initGA, trackPage, trackPageMeta } from "@/lib/analytics";

axios.defaults.withCredentials = true;

function AppRouter() {
  const location = useLocation();
  const firstNav = useRef(true);

  useEffect(() => {
    trackPage(location.pathname + location.hash);
    // The static Meta Pixel tag already fires PageView on initial load;
    // only fire on subsequent SPA navigations to avoid double-counting.
    if (firstNav.current) {
      firstNav.current = false;
    } else {
      trackPageMeta();
    }
  }, [location.pathname, location.hash]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/listings/:key" element={<ListingDetail />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:id" element={<BlogPost />} />
      <Route path="/living-in-lagoon-city" element={<Navigate to="/community/neighbourhood" replace />} />
      <Route path="/community" element={<Navigate to="/community/neighbourhood" replace />} />
      <Route path="/community/neighbourhood" element={<Neighbourhood />} />
      <Route path="/community/local-businesses" element={<LocalBusinesses />} />
      <Route path="/community/events" element={<CommunityEvents />} />
      <Route path="/buying-in-lagoon-city" element={<BuyersGuide />} />
      <Route path="/calculators" element={<Calculators />} />
      <Route path="/neighbourhood-buzzer" element={<NeighbourhoodBuzzer />} />
      <Route path="/home-evaluation" element={<HomeEvaluation />} />
      <Route path="/buying-tips" element={<BuyingTips />} />
      <Route path="/buying-tips/:slug" element={<BuyingTipDetail />} />
      <Route path="/selling-tips" element={<SellingTips />} />
      <Route path="/selling-tips/:slug" element={<SellingTipDetail />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    initGA();
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <HelmetProvider>
      <div className="App grain">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-right" />
      </div>
    </HelmetProvider>
  );
}

export default App;
